import {
  ActionButtonProps,
  Box,
  BoxProps,
  cardNumber,
  ColorOptions,
  Form,
  Image,
  ImageProps,
  Loader as MLoader,
  Spacer,
  Text,
  TextProps
} from '@jfs/ui-new'
import { Icon } from '@jfs/ui-new/src/comps/IconNew'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { FlatList, ScrollView } from 'react-native'
import { Button } from '../../comps/Button'
import { CardNumberTextInput } from '../../comps/CardNumberTextInput'
import { CardTypes } from '../../comps/CardNumberTextInput/types'

import { EmptyView } from '../../comps/EmptyView'
import { Modal } from '../../comps/Modal'
import { NumberTextInput } from '../../comps/NumberTextInput'
import { PaySecurelyButton } from '../../comps/PaySecurelyButton'
import { TextInputToolTip } from '../../comps/TextInputToolTip'
import { useLazyApiData, useStores } from '../../data/store'
import { convertHexToRGBA, getKeyboardType, isIFrame, returnRegexSchema } from '../../utils'
import { PAYMENT_MODE_SUBTYPES, PAYMENT_MODE_TYPE, THEME_PADDING, windowConfig } from '../../utils/constants'
import { MobileProcessing } from '../PaymentProcesseing/MobileProcessing'

const LogoImage = ({ imgUrl, type, color }: { imgUrl: string; type: number; color: any }) => {
  const [imageUrl, setImageUrl] = useState(imgUrl)
  useEffect(() => {
    setImageUrl(imgUrl)
  }, [imgUrl])
  const onError = useCallback(() => {
    setImageUrl('')
  }, [])
  if (imageUrl) {
    return <Image height={32} width={32} source={imageUrl} onError={onError} />
  }
  return <Icon id={type === PAYMENT_MODE_SUBTYPES.NB ? 'BANK' : 'WALLET'} color={color} size={28} />
}

const Loader = ({ state, onRetry, message, show = false, theme }: any) => {
  if (!show) {
    return null
  }
  return <EmptyView state={state} onRetry={onRetry} message={message} theme={theme} />
}

const PayLater: FC = () => {
  const [item, setItem] = useState({
    id: 0,
    text: '',
    imageUrl: '',
  })
  const { getInstruments, getInstrumentsList, amount, addPaymentMethod, getInstrumentsInputData, theme } = useStores(
    (root) => ({
      getInstruments: root.getInsturmentsApi,
      getInstrumentsList: root.getInstrumentsList,
      amount: root.transaction.amount.payableAmount,
      getInstrumentsInputData: root.getInstrumentInputData,
      addPaymentMethod: root.transaction.addPaymentMethod,
      theme: root?.config?.merchantTheme,
    }),
  )

  const style = styles(theme)
  const [showModal, setShowMoal] = useState(false)
  const [lazyInsrtumentsApi, { state: instrumentsState, data: instrumentsData }] = useLazyApiData(getInstruments)
  const [showPaymentProcessModal, setShowPaymentProcessModal] = useState(false)

  useEffect(() => {
    if (getInstrumentsList(PAYMENT_MODE_SUBTYPES.PAYLATER).length === 0) {
      lazyInsrtumentsApi(PAYMENT_MODE_SUBTYPES.PAYLATER)
    }
  }, [getInstrumentsList, lazyInsrtumentsApi])

  if (instrumentsState === 'pending' || instrumentsState === 'rejected') {
    return (
      <Loader
        state={instrumentsState}
        onRetry={lazyInsrtumentsApi}
        message={instrumentsData?.message}
        show={instrumentsState === 'pending' || instrumentsState === 'rejected'}
        theme={theme}
      />
    )
  }

  const onClickPayLaterItem = (item: any) => {
    setItem(item)
    setShowMoal(true)
  }

  const renderListItem = ({ item, index }: any) => {
    return (
      <>
        <Box
          underlayColor={convertHexToRGBA(theme?.brandColor, 10) as ColorOptions}
          testID={`bankId-${item.id}`}
          paddingVertical={16}
          paddingHorizontal={THEME_PADDING.HORIZONTAL_PADDING}
          flexDirection="row"
          onPress={() => onClickPayLaterItem(item)}>
          <Box justifyContent="center">
            <LogoImage
              imgUrl={item?.imageUrl ?? ''}
              type={PAYMENT_MODE_SUBTYPES.PAYLATER}
              color={theme?.brandColor as ColorOptions}
            />
          </Box>
          <Box justifyContent="center" paddingLeft={16}>
            <Text {...style.title}>{item.text}</Text>
          </Box>
        </Box>
        <Box {...style.separatorStyle} />
      </>
    )
  }

  const addPayment = () => {
    addPaymentMethod({ ...item, name: item.text, paymentType: 'PayLater', inputData: [] } as any)
  }
  const result = getInstrumentsList(PAYMENT_MODE_SUBTYPES.PAYLATER)
  return (
    <Box flex={1}>
      <Box paddingHorizontal={THEME_PADDING.HORIZONTAL_PADDING} paddingVertical={8}>
        <Text {...style.title}>Pay Using</Text>
        <Spacer size={8} />
      </Box>
      <FlatList data={result} renderItem={renderListItem} />
      {showModal && (
        <MobileModel
          selectedItem={item?.id}
          showModal={showModal}
          data={getInstrumentsInputData(item?.id)}
          amount={amount}
          onDismiss={() => {
            setShowMoal(false)
          }}
          setShowPaymentProcessModal={() => setShowPaymentProcessModal(true)}
          addPayment={addPayment}
          theme={theme}
        />
      )}
      {showPaymentProcessModal && (
        <MobileProcessing setShow={setShowPaymentProcessModal} show={showPaymentProcessModal} />
      )}
    </Box>
  )
}

const MobileModel = ({
  selectedItem,
  showModal,
  amount,
  onDismiss,
  setShowPaymentProcessModal,
  addPayment,
  data: selectedItemData,
  theme,
}: {
  selectedItem: number
  showModal: boolean
  amount: string
  onDismiss: () => void
  setShowPaymentProcessModal: () => void
  addPayment: () => void
  data: any
  theme: any
}) => {
  const {
    validateOfferApi,
    applyOffer,
    preProcess,
    updateInputData,
    getInstrumentsInputData,
    offerApplied,
    updateIsOfferApplied,
    isValidOffer,
    uniqueWindowId,
  } = useStores((root) => ({
    validateOfferApi: root.offerValidateApi,
    applyOffer: root.offerTxnApplyApi,
    preProcess: root.preProcessApi,
    updateInputData: root.updateInputData,
    getInstrumentsInputData: root.getInstrumentInputData,
    offerApplied: root.isOfferApplied,
    updateIsOfferApplied: root.transaction.updateIsOfferApplied,
    isValidOffer: root.transaction.isValidOffer,
    uniqueWindowId: root.config.uniqueWindowId,
  }))
  const [value, setValue] = useState({})
  const [show, setShow] = useState(true)
  const [offerInfo, setOfferInfo] = useState({ type: '', message: '' })
  const [errorMessage, setErrorMessage] = useState('')
  const [isRetry, setIsRetry] = useState(false)
  const personalDetailRef = useRef<any>()

  const isOfferApplied = offerApplied(PAYMENT_MODE_SUBTYPES.PAYLATER)
  const [lazyValidateOfferApi, { state: validateOfferApiState, data: validateOfferApiData }] =
    useLazyApiData(validateOfferApi)
  const [lazyPreprocessApi, { state: preprocessState, data: preprocessData }] = useLazyApiData(preProcess)
  const [lazyApplyOffer, { state: applyOfferState, data: applyOfferData }] = useLazyApiData(applyOffer)

  const { REACT_APP_URL = '', REACT_APP_ENDPOINT = '' } = process.env

  const moveToPaymentProcessing = useCallback(() => {
    const loaderUrl = REACT_APP_ENDPOINT
      ? `${REACT_APP_URL}/${REACT_APP_ENDPOINT}/loader.html`
      : `${REACT_APP_URL}/loader.html`
    if (!isIFrame()) {
      window.currentChild = window
    } else {
      window.currentChild = window.open(loaderUrl, `jiopayWindow-${uniqueWindowId}`, windowConfig)
      window.currentChild?.focus()
    }
    onDismiss()
    setShowPaymentProcessModal()
  }, [REACT_APP_ENDPOINT, REACT_APP_URL, onDismiss, setShowPaymentProcessModal, uniqueWindowId])

  useEffect(() => {
    if (applyOfferState === 'fulfilled') {
      moveToPaymentProcessing()
    } else if (applyOfferState === 'rejected') {
      // const data = applyOfferData
      setOfferInfo({ type: 'error', message: 'Offer not applied' })
    }
  }, [applyOfferData, applyOfferState, moveToPaymentProcessing])

  useEffect(() => {
    if (validateOfferApiState === 'fulfilled') {
      setOfferInfo({ type: 'info', message: 'Offer applied' })
    } else if (validateOfferApiState === 'rejected') {
      // const err = validateOfferApiData
      setOfferInfo({ type: 'error', message: 'Offer not applied' })
    }
  }, [validateOfferApiData, validateOfferApiState])

  useEffect(() => {
    if (preprocessState === 'fulfilled') {
      if (isOfferApplied && !applyOfferState && isValidOffer) {
        addPayment()
        lazyApplyOffer()
      } else {
        updateIsOfferApplied(false)
        addPayment()
        moveToPaymentProcessing()
      }
    } else if (preprocessState === 'rejected') {
      const error = preprocessData
      setErrorMessage(error?.message || 'Sorry, your payment was declined')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preprocessState])

  useEffect(() => {
    if (isOfferApplied) {
      lazyValidateOfferApi(
        { bankId: selectedItem },
        {
          type: PAYMENT_MODE_TYPE.PURCHASE,
          subType: PAYMENT_MODE_SUBTYPES.PAYLATER,
        },
      )
    }
    // return removeOffer
  }, [isOfferApplied, lazyValidateOfferApi, selectedItem])

  const onClickPay = (values: any, value: any) => {
    updateInputData(String(selectedItem), value)
    setErrorMessage('')
    setIsRetry(false)
    lazyPreprocessApi(values, getInstrumentsInputData(selectedItem), false)
  }

  const onChange = (data: string, type: string) => {
    setValue({
      ...value,
      [type]: data,
    })
  }

  const style = styles(theme)
  const onClose = () => {
    setShow(false)
    onDismiss()
  }

  const modalLoader = (style: any, name: string) => {
    const text = `Please wait... \n We are checking your eligibility with \n ${name}`
    return (
      <Box paddingVertical={24} backgroundColor={theme?.bodyBgColor as ColorOptions}>
        <MLoader type={'large'} color={theme?.brandColor as ColorOptions} testID={'redirectionLoaderId'} />
        <Spacer size={24} />
        <Text {...style.loadText}>{text}</Text>
      </Box>
    )
  }
  const colorTheme = {
    placeholderTextColor: convertHexToRGBA(theme?.bodyTextColor, 50) as ColorOptions,
    color: theme?.bodyTextColor as ColorOptions,
    bottomLineColor: theme?.brandColor as ColorOptions,
    onFocusBottomLineColor: theme?.brandColor as ColorOptions,
    tintColor: theme?.brandColor as ColorOptions,
    selectionColor: theme?.brandColor as ColorOptions,
  }

  const formData = (inputData: any) => {
    const retData =
      inputData.map((item: any, index: any) => {
        if (item.id === 'cardNumber') {
          return {
            ...colorTheme,
            ComponentName: CardNumberTextInput,
            error: '',
            rcColor: theme.brandColor,
            type: CardTypes.CREDIT_CARD,
            fieldName: item.id,
            validationSchema: cardNumber,
            onCustomBlur: () => { },
            onChangeText: (text: any) => onChange(text, item.id),
          }
        } else if (item.id === 'mobile') {
          return {
            theme,
            ComponentName: NumberTextInput,
            fieldName: item.id,
            testID: `inputField-${index}`,
            label: item.labelString,
            validationSchema: returnRegexSchema(new RegExp(item.validationPattern), item.errorMessage),
          }
        }
        return {
          ...colorTheme,
          ComponentName: TextInputToolTip,
          theme,
          fieldName: item.id,
          validationSchema: returnRegexSchema(new RegExp(item.validationPattern), item.errorMessage),
          keyboardType: getKeyboardType(item.id),
          testID: `inputField-${index}`,
          label: item.labelString !== null ? item.labelString : '',
          fontSize: 14,
          fontFamily: 'jiotype-medium',
          fontWeight: '500',
          infoText: item.info,
          infoIconColor: theme?.brandColor as ColorOptions,
          bottomLineColor: theme?.brandColor as ColorOptions,
          tintColor: theme?.brandColor as ColorOptions,
        }
      }) ?? []

    return [
      {
        formItems: [...retData],
        onSubmit: () => {
          return 'end'
        },
      },
    ]
  }
  const renderActionButton = ({ values, isValid }: ActionButtonProps) => {
    return (
      <Box justifyContent="center" alignItems="center">
        <>
          <Spacer size={16} />
        </>
        <PaySecurelyButton
          disabled={!isValid}
          isLoading={preprocessState === 'pending' || validateOfferApiState === 'pending'}
          amount={amount}
          testID={'paySecurelyBtn'}
          onPress={async () => {
            personalDetailRef.current = {
              email: values.email,
              mobile: values.mobile,
            }
            // setRetryBtnClicked('')
            onClickPay(values, value)
          }}
        />
      </Box>
    )
  }

  const modalBody = () => {
    const inputData = selectedItemData.inputData ?? []
    const filteredData = inputData.filter((item: any) => item.usage === 'userInput')
    return (
      <ScrollView
        keyboardShouldPersistTaps="always"
        style={{
          flexGrow: 1,
          backgroundColor: theme?.bodyBgColor as ColorOptions,
          height: filteredData.length * 80 + 88,
        }}>
        <Form formData={formData(filteredData)} initialValues={{}} actionButtons={renderActionButton as any} />
      </ScrollView>
    )
  }

  if (!showModal) {
    return null
  }

  if (errorMessage && !isRetry) {
    return (
      <Modal type={'Bottom'} show={show} onDismiss={onClose} theme={theme}>
        <Box paddingHorizontal={16} paddingVertical={24}>
          <Spacer size={30} />
          <Box justifyContent="center" flexDirection="row">
            <Icon id="INFO" size={40} color={theme?.brandColor as ColorOptions} />
          </Box>
          <Spacer size={13} />
          <Text {...style.errorModalText}>{errorMessage}</Text>
          <Spacer size={32} />

          <Button
            type="secondary"
            isLoading={false}
            title={'Change Payment Method'}
            isFull={true}
            testID="backToPaymentTest"
            onPress={() => onDismiss()}
          />
          <Spacer size={16} />
          <Button
            type="secondary"
            isLoading={false}
            title={'Retry with another phone number'}
            isFull={true}
            testID="retryPayment"
            onPress={() => {
              setIsRetry(true)
            }}
          />
          {/* <SecondayButton
            isLoader={false}
            title="Retry with another phone number"
            theme={theme}
            onPress={() => {
              // onClose()
              setIsRetry(true)
            }}
          /> */}
          <Spacer size={16} />
        </Box>
      </Modal>
    )
  }

  return (
    <Modal type={'Bottom'} show={show} onDismiss={onClose} theme={theme}>
      <Box paddingHorizontal={16} paddingVertical={22}>
        <Box
          justifyContent="space-between"
          underlayColor={convertHexToRGBA(theme?.brandColor, 10) as ColorOptions}
          testID={`bankId-${selectedItemData.id}`}
          flexDirection="row">
          <Box flexDirection="row">
            <Box justifyContent="center">
              <LogoImage
                imgUrl={selectedItemData?.imageUrl ?? ''}
                type={PAYMENT_MODE_SUBTYPES.PAYLATER}
                color={theme?.brandColor as ColorOptions}
              />
            </Box>
            <Box justifyContent="center" paddingLeft={16}>
              <Text {...style.title}>{selectedItemData.text}</Text>
              <>
                <Spacer size={8} />
                <Text {...style.subTitle} color={offerInfo.type === 'error' ? theme.bodyTextColor : theme.brandColor}>
                  {offerInfo.message}
                </Text>
              </>
            </Box>
          </Box>
          <Box
            onPress={onClose}
            testID="close-modal"
            underlayColor={convertHexToRGBA(theme?.brandColor, 10) as ColorOptions}>
            <Icon id={'CLOSE'} size={16} color={theme?.brandColor as ColorOptions} />
          </Box>
        </Box>
        {preprocessState !== 'pending' && validateOfferApiState !== 'pending' && modalBody()}
        {(preprocessState === 'pending' || validateOfferApiState === 'pending') &&
          modalLoader(style, selectedItemData.text)}
      </Box>
    </Modal>
  )
}

const styles = (theme: any) => ({
  imageView: {
    height: 36,
    width: 36,
  } as ImageProps,
  title: {
    fontSize: 14,
    fontFamily: 'jioType-Medium',
    color: theme.bodyTextColor,
  },
  subTitle: {
    fontSize: 12,
    fontFamily: 'jioType-Medium',
    color: convertHexToRGBA(theme?.bodyTextColor, 50) as ColorOptions,
  },
  separatorStyle: {
    height: 1,
    backgroundColor: convertHexToRGBA(theme?.brandColor, 10) as ColorOptions,
  } as BoxProps,
  mobContainer: {
    marginTop: 32,
  } as BoxProps,
  plusNineOneText: {
    color: theme.bodyTextColor,
    fontSize: 14,
    fontFamily: 'jiotype-medium',
    fontWeight: '700',
  } as TextProps,
  errorText: {
    paddingVertical: 4,
    color: 'ERROR',
    fontSize: 12,
    fontFamily: 'jiotype-medium',
    fontWeight: '500',
  } as TextProps,
  loadText: {
    fontSize: 12,
    paddingTop: 24,
    fontFamily: 'jioType-Medium',
    textAlign: 'center',
    color: convertHexToRGBA(theme?.bodyTextColor, 50) as ColorOptions,
  } as TextProps,
  buttonbg: {
    padding: 16,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    height: 48,
    borderWidth: 1,
    borderColor: theme?.brandColor as ColorOptions,
    underlayColor: theme?.brandColor as ColorOptions,
  } as BoxProps,
  errorModalText: {
    fontSize: 14,
    fontFamily: 'jioType-Medium',
    color: theme.bodyTextColor,
    textAlign: 'center',
  } as TextProps,
})
export default PayLater
